import React from 'react';
import { WarningCircle } from '@phosphor-icons/react';

interface AtRiskRow {
    id: string | number;
    status: string;
    actualProgress: number;
    expectedProgress: number;
    startDate?: string;
    endDate?: string;
    programName?: string;
    completedSessions: number;
    totalSessions: number;
    coacheeName?: string;
    coachName?: string;
    daysOverdue: number;
}

interface CoachingAtRiskEngagementsWidgetProps {
    data?: AtRiskRow[] | null;
    loading?: boolean;
    error?: string;
}

const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

export const CoachingAtRiskEngagementsWidget: React.FC<CoachingAtRiskEngagementsWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex-1">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <WarningCircle className="w-8 h-8" />
                        <span className="text-sm">No at-risk engagements</span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-1">
                        {/* Summary stats */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="text-center p-2 bg-red-50 rounded-lg">
                                <div className="text-lg font-bold text-red-600">{rows.filter(r => Number(r.daysOverdue) > 0).length}</div>
                                <div className="text-[10px] text-gray-400">Overdue</div>
                            </div>
                            <div className="text-center p-2 bg-amber-50 rounded-lg">
                                <div className="text-lg font-bold text-amber-600">{rows.filter(r => Number(r.daysOverdue) <= 0).length}</div>
                                <div className="text-[10px] text-gray-400">Behind Schedule</div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded-lg">
                                <div className="text-lg font-bold text-gray-600">{rows.length}</div>
                                <div className="text-[10px] text-gray-400">Total At Risk</div>
                            </div>
                        </div>

                        {/* Row list */}
                        <div className="flex-1 overflow-auto space-y-2">
                            {rows.map((row, idx) => {
                                const overdue = Number(row.daysOverdue) || 0;
                                const isOverdue = overdue > 0;
                                const actual = Number(row.actualProgress) || 0;
                                const expected = Number(row.expectedProgress) || 0;

                                return (
                                    <div key={row.id || idx} className={`flex items-center gap-3 p-2.5 rounded-lg ${isOverdue ? 'bg-red-50/60' : 'bg-gray-50/60'}`}>
                                        {/* Avatar */}
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                            isOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {getInitials(row.coacheeName || '??')}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-0.5">
                                                <span className="text-sm text-gray-800 font-medium truncate">{row.coacheeName || '\u2014'}</span>
                                                {isOverdue ? (
                                                    <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium shrink-0 ml-2">{overdue}d overdue</span>
                                                ) : (
                                                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium shrink-0 ml-2">Behind</span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-gray-500 mb-1 truncate">
                                                {row.programName || 'No program'} &middot; Coach: {row.coachName || '\u2014'}
                                            </div>
                                            {/* Progress bar */}
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-500 ${actual < 30 ? 'bg-red-500' : 'bg-amber-500'}`}
                                                        style={{ width: `${Math.min(actual, 100)}%` }}
                                                    />
                                                </div>
                                                <span className="text-[10px] text-gray-500 shrink-0">{actual}% / {expected}%</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
